import React, {
  useState,
  useEffect,
  useContext,
  useRef,
  useCallback,
  useMemo,
} from "react";
import styled from "styled-components";

import { baseConfig } from "./../../../axiosConfig";
import { Context } from "./../../../contexts/Store";

import docIcon from "../../../assets/icons/doc-icon.svg";
import jpgIcon from "../../../assets/icons/jpg-icon.svg";
import pdfIcon from "../../../assets/icons/pdf-icon.svg";
import pngIcon from "../../../assets/icons/png-icon.svg";
import pptIcon from "../../../assets/icons/ppt-icon.svg";
import xlsxIcon from "../../../assets/icons/xlsx-icon.svg";
import jpegIcon from "../../../assets/icons/jpeg-icon.svg";
import mp4Icon from "../../../assets/icons/mp4-icon.svg";
import chevronUp from "../../../assets/icons/chevron-up.svg";
import chevronDown from "../../../assets/icons/chevron-down.svg";
import tickIconGreen from "../../../assets/icons/tick.svg";
import fileUploadIcon from "../../../assets/icons/file_upload.svg";
import closeIcon from "../../../assets/icons/close.png";
import othIcon from "../../../assets/icons/oth.png";
import reUploadIcon from "../../../assets/icons/re-upload-icon.svg";
import downArrow from "../../../assets/icons/arrow-bottom.svg";
import { useNavigate, useParams } from "react-router-dom";
import Loader from "../../includes/loader/Loader";
import {
  formatFileSize,
  normalizeFileType,
  getAcceptedFileTypes,
  timeFormatter,
  getFileIcon,
} from "./fileUtils";

// ============= UTILITY FUNCTIONS =============
// Process submitted files by chapter - CORRECTED LOGIC
const processSubmittedFilesByChapter = (submittedFiles, globalStatus) => {
  const chapterSubmissions = {};

  submittedFiles.forEach((submission) => {
    const chapterId = submission.bln_draft
      ? "draft"
      : submission.bln_final_submission
      ? "final"
      : submission.chapter;

    if (!chapterSubmissions[chapterId]) {
      chapterSubmissions[chapterId] = [];
    }

    chapterSubmissions[chapterId].push({
      version: submission.version,
      chapter: chapterId,
      feedback: submission.feedback || "",
      upload: submission.upload || null,
      isLatest: submission.is_latest || false,
      files: submission.files.map((file) => ({
        fileName: file.file_name,
        fileUrl: file.file_url,
        fileType: file.feedback_file_type || file.file_name?.split(".").pop(),
        uploadedDate: file.submitted_date,
      })),
      submittedDate: submission.submitted_date,
      status: "", // Will be set below
      feedbackDate: submission.feedback_date,
      feedback_file_name: submission.feedback_file_name,
      feedback_file_type: submission.feedback_feedback_type,
    });
  });

  // Sort versions by latest first and assign status
  Object.keys(chapterSubmissions).forEach((chapterId) => {
    // Find which submission has is_latest: true
    const latestSubmission = chapterSubmissions[chapterId].find(
      (sub) => sub.isLatest === true
    );

    // Assign status based on is_latest flag
    chapterSubmissions[chapterId].forEach((submission) => {
      if (submission.isLatest === true) {
        // Only the submission with is_latest: true gets global status
        submission.status = globalStatus;
        submission.isLatestForChapter = true;
      } else {
        // For others, we need to determine based on chapter progression logic
        // If this chapter has unlocked the next chapter, it was probably "Approved"
        // Otherwise, it's "Returned"
        submission.status = "Returned"; // Default for non-latest
        submission.isLatestForChapter = false;

        // If this is the highest version that's not latest, and next chapters exist,
        // it might have been "Approved" previously
        const maxNonLatestVersion = [
          ...chapterSubmissions[chapterId]
            .filter((sub) => !sub.isLatest)
            .map((sub) => sub.version),
        ];

        if (
          submission.version ===
            maxNonLatestVersion[maxNonLatestVersion.length - 1] &&
          latestSubmission?.chapter != chapterId
        ) {
          // This was the previous latest - check if it should be "Approved"
          // Logic: if current global status suggests progression happened,
          // this previous submission was likely "Approved"

          submission.status = "Approved";
        }
      }
    });
  });

  return chapterSubmissions;
};

// NEW: Helper function to determine chapter status and unlock logic
const determineChapterStatuses = (
  fileArray,
  processedSubmissions,
  globalStatus
) => {
  const statuses = {};

  fileArray.forEach((chapter, index) => {
    const chapterId =
      chapter.sectionName === "Draft Submission"
        ? "draft"
        : chapter.sectionName === "Final Submission"
        ? "final"
        : chapter.id;

    const chapterSubmissions = processedSubmissions[chapterId] || [];
    const hasSubmissions = chapterSubmissions.length > 0;

    if (index === 0) {
      // Chapter 1 is always active initially
      const latestSubmission = chapterSubmissions.find(
        (sub) => sub.isLatest === true
      );
      statuses[chapter.id] = latestSubmission
        ? latestSubmission.status
        : "active";
    } else if (chapter.sectionName === "Draft Submission") {
      // Draft available after all regular chapters are approved
      const allChaptersApproved = fileArray
        .slice(0, fileArray.length - 2)
        .every((ch) => {
          const chId = ch.id;
          const submissions = processedSubmissions[chId];
          if (!submissions || submissions.length === 0) return false;

          // Check if any submission in this chapter has "Approved" status
          return submissions.some((sub) => sub.status === "Approved");
        });

      if (allChaptersApproved) {
        const latestSubmission = chapterSubmissions.find(
          (sub) => sub.isLatest === true
        );
        statuses[chapter.id] = latestSubmission
          ? latestSubmission.status
          : "active";
      } else {
        statuses[chapter.id] = "locked";
      }
    } else if (chapter.sectionName === "Final Submission") {
      // Final available after draft is approved
      const draftSubmissions = processedSubmissions["draft"];
      const draftApproved =
        draftSubmissions &&
        draftSubmissions.some((sub) => sub.status === "Approved");

      if (draftApproved) {
        const latestSubmission = chapterSubmissions.find(
          (sub) => sub.isLatest === true
        );
        statuses[chapter.id] = latestSubmission
          ? latestSubmission.status
          : "active";
      } else {
        statuses[chapter.id] = "locked";
      }
    } else {
      // Regular chapters - unlock based on previous chapter approval
      const prevChapter = fileArray[index - 1];
      const prevChapterId = prevChapter.id;
      const prevSubmissions = processedSubmissions[prevChapterId];
      const prevApproved =
        prevSubmissions &&
        prevSubmissions.some((sub) => sub.status === "Approved");

      if (prevApproved) {
        const latestSubmission = chapterSubmissions.find(
          (sub) => sub.isLatest === true
        );
        statuses[chapter.id] = latestSubmission
          ? latestSubmission.status
          : "active";
      } else {
        statuses[chapter.id] = "locked";
      }
    }
  });

  return statuses;
};

// NEW: Helper function to get latest submission status for a specific chapter
const getLatestSubmissionStatus = (
  selectedFile,
  processedSubmissions,
  globalStatus
) => {
  if (!selectedFile) return null;

  const chapterId =
    selectedFile.sectionName === "Draft Submission"
      ? "draft"
      : selectedFile.sectionName === "Final Submission"
      ? "final"
      : selectedFile.id;

  const submissions = processedSubmissions[chapterId];
  if (!submissions || submissions.length === 0) return null;
  const latestSubmission = submissions.find(
    (sub) => sub.isLatest === true || sub.status === "Approved"
  );

  return latestSubmission ? latestSubmission.status : null;
};

// Enhanced LoadedFileDiv with proper file tracking
const LoadedFileDiv = React.memo(
  ({ file, onDelete, selectedFileId, fileTypeToDelete }) => {
    const formattedSize = useMemo(
      () => formatFileSize(file.file?.size),
      [file.file?.size]
    );
    const extension = useMemo(
      () => file.file?.name?.split(".").pop(),
      [file.file?.name]
    );

    const handleDelete = useCallback(() => {
      onDelete(file.id, fileTypeToDelete, selectedFileId);
    }, [file.id, onDelete, fileTypeToDelete, selectedFileId]);

    const getFileTypeColor = (selectedFile, itemFileType) => {
      // Determine background color based on file type
      if (selectedFile === "Pdf" && itemFileType === "Pdf") return "#FFCCCC";
      if (
        ["Pptx", "Ppt"].includes(selectedFile) &&
        ["Pptx", "Ppt"].includes(itemFileType)
      )
        return "#FF7B0780";
      if (
        ["Word", "Docx", "Doc"].includes(selectedFile) &&
        ["Word", "Docx", "Doc"].includes(itemFileType)
      )
        return "#D3E5FF";
      if (
        selectedFile === "Similarity Report" &&
        itemFileType === "Similarity Report"
      )
        return "#B48CCC50";
      // Add more color mappings as needed
      return "#b48ccc50";
    };

    return (
      <FileCard>
        <FileCardLink
          href={file.file ? URL.createObjectURL(file.file) : file.file_url}
          target="_blank"
        >
          <FileCardContent>
            <FileIcon src={getFileIcon(extension)} />
            <FileInfo>
              <FileName>{file.file?.name || file.file_url}</FileName>
              <FileMeta>
                {formattedSize && <FileSize>{formattedSize}</FileSize>}
                {file.date && <FileDate>{file.date}</FileDate>}
              </FileMeta>
            </FileInfo>
          </FileCardContent>
        </FileCardLink>
        {file.file?.size && (
          <DeleteButton>
            <FileIcon
              src={closeIcon}
              style={{ width: "13px", height: "13px", cursor: "pointer" }}
              onClick={handleDelete}
            />
          </DeleteButton>
        )}
      </FileCard>
    );
  }
);

const SubmitChapterWise = ({
  setSinglePage,
  refreshApi,
  assignmentDetails,
  fileArray,
  selectedFile,
  setFileArray,
  setSelectedFile,
}) => {
  const { state } = useContext(Context);
  const { id } = useParams();
  const navigate = useNavigate();

  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [submitButton, setSubmitButton] = useState(false);
  const [similarityReport, setSimilarityReport] = useState({
    file: null,
    percentage: null,
    status: null,
  });
  const [isDragging, setIsDragging] = useState(false);
  const [chapterStatuses, setChapterStatuses] = useState({});
  const [expandedVersions, setExpandedVersions] = useState({});
  const [processedSubmissions, setProcessedSubmissions] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const dragCounter = useRef(0);
  const fileInputRef = useRef(null);

  // NEW: Get global status from assignment details
  const globalStatus = assignmentDetails?.status;

  // Process submitted files when component mounts or data changes
  useEffect(() => {
    if (assignmentDetails?.submitted_files) {
      const processed = processSubmittedFilesByChapter(
        assignmentDetails.submitted_files,
        globalStatus
      );
      setProcessedSubmissions(processed);
    }
  }, [assignmentDetails, globalStatus]);

  // NEW: Initialize chapter statuses based on new logic
  useEffect(() => {
    if (fileArray?.length > 0) {
      const statuses = determineChapterStatuses(
        fileArray,
        processedSubmissions,
        globalStatus
      );
      setChapterStatuses(statuses);
    }
  }, [fileArray, processedSubmissions, globalStatus]);

  // Calculate remaining files needed for current chapter
  const remainingFiles = useMemo(() => {
    if (!selectedFile?.fileRequirement) return {};

    const uploaded = uploadedFiles.filter((file) =>
      fileArray.find((ch) => ch.id === selectedFile.id)
    );

    const remaining = {};
    selectedFile.fileRequirement.forEach((req) => {
      const uploadedCount = uploaded.filter(
        (file) => file.fileType === req.file_type
      ).length;
      remaining[req.file_type] = Math.max(0, req.count - uploadedCount);
    });

    return remaining;
  }, [selectedFile, uploadedFiles, fileArray]);

  // NEW: Check if submit button should be enabled with updated logic
  useEffect(() => {
    const latestStatus = getLatestSubmissionStatus(
      selectedFile,
      processedSubmissions,
      globalStatus
    );
    const isChapterCompleted =
      latestStatus === "Approved" || latestStatus === "Submitted";

    if (isChapterCompleted) {
      setSubmitButton(false);
      return;
    }

    const totalRemaining = Object.values(remainingFiles).reduce(
      (acc, count) => acc + count,
      0
    );
    const hasSimilarityReport = selectedFile?.fileRequirement?.some(
      (req) => req.file_type === "Similarity Report"
    );
    const similarityValid = hasSimilarityReport
      ? similarityReport.file && similarityReport.status === "valid"
      : true;

    setSubmitButton(totalRemaining === 0 && similarityValid);
  }, [
    remainingFiles,
    similarityReport,
    selectedFile,
    processedSubmissions,
    globalStatus,
  ]);

  const submitAssignmentFile = useCallback(() => {
    let accessToken = state.user_details.access_token;
    const formdata = new FormData();

    uploadedFiles.forEach((item) => {
      formdata.append("submitted_file", item.file);
    });

    if (selectedFile.sectionName === "Draft Submission") {
      formdata.append("bln_draft", true);
    } else if (selectedFile.sectionName === "Final Submission") {
      formdata.append("bln_final_submission", true);
    } else {
      formdata.append("chapter", selectedFile.id);
    }

    setIsLoading(true);

    baseConfig
      .post(
        `/students/assignment-submit/${id || assignmentDetails.assignment_id}/`,
        formdata,
        {
          headers: {
            Authorization: "Bearer " + accessToken,
            "Content-Type": "multipart/form-data",
          },
        }
      )
      .then((response) => {
        const { StatusCode } = response.data;
        if (StatusCode === 6000) {
          // Update chapter status to Pending
          setChapterStatuses((prev) => ({
            ...prev,
            [selectedFile.id]: "Pending",
          }));
          setIsLoading(false);
          // Clear uploaded files for current chapter
          setUploadedFiles([]);
          setSimilarityReport({ file: null, percentage: null, status: null });

          setSinglePage && setSinglePage(false);
          refreshApi && refreshApi();
          if (!setSinglePage && !refreshApi) {
            navigate("/assignments/");
          }
        } else {
          alert("Contact LMS");
          setIsLoading(false);
        }
      })
      .catch((error) => {
        if (error?.response?.data?.code === "token_not_valid") {
          localStorage.clear();
          window.location = "/";
        }
        setIsLoading(false);
        console.error("Submit error:", error);
        alert("Contact LMS");
      });
  }, [
    uploadedFiles,
    similarityReport,
    id,
    assignmentDetails,
    state.user_details.access_token,
    setSinglePage,
    refreshApi,
    navigate,
    selectedFile,
  ]);

  const handleFileSelection = useCallback(
    (chapter) => {
      if (chapterStatuses[chapter.id] === "locked") {
        alert("Please complete previous chapters first.");
        return;
      }
      setSelectedFile(chapter);
      // Clear files when switching chapters
      setUploadedFiles([]);
      setSimilarityReport({ file: null, percentage: null, status: null });
    },
    [chapterStatuses, setSelectedFile]
  );

  const handleVersionToggle = useCallback((chapterId, version) => {
    const key = `${chapterId}-${version}`;
    setExpandedVersions((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  }, []);

  // NEW: Get submissions for selected chapter only
  const getSelectedChapterSubmissions = useCallback(() => {
    if (!selectedFile) return [];

    const chapterId =
      selectedFile.sectionName === "Draft Submission"
        ? "draft"
        : selectedFile.sectionName === "Final Submission"
        ? "final"
        : selectedFile.id;

    return processedSubmissions[chapterId] || [];
  }, [selectedFile, processedSubmissions]);

  const handleReport = useCallback(
    async (e) => {
      let accessToken = state.user_details.access_token;
      const formData = new FormData();
      formData.append("submitted_file", e.target.files[0]);

      try {
        const { data } = await baseConfig.post(
          "/students/similarity-check/",
          formData,
          {
            headers: {
              Authorization: "Bearer " + accessToken,
            },
          }
        );

        setSimilarityReport({
          file: e.target.files[0],
          status: data.data.status,
          percentage: data.data.percentage,
        });

        if (data.data.status === "valid") {
          const now = new Date();
          const formattedTime = now.toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "numeric",
            hour12: true,
          });

          const newFileObject = {
            id: Date.now() + Math.random(),
            file: e.target.files[0],
            date: formattedTime,
            fileType: "Similarity Report",
            chapterId: selectedFile.id,
          };

          setUploadedFiles((prev) => [...prev, newFileObject]);
        }
      } catch (error) {
        console.error("Similarity check error:", error);
        alert("Error sending Similarity Report");
      }

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
    [state.user_details.access_token, selectedFile]
  );

  const handleFileUpload = useCallback(
    (e, fileType) => {
      if (e && typeof e.preventDefault === "function") {
        e.preventDefault();
      }

      const files = Array.from(e.target.files || []);
      if (files.length === 0) return;

      const file = files[0];

      // Check if more files of this type are needed
      if (remainingFiles[fileType] <= 0) {
        alert(`No more ${fileType} files needed for this chapter.`);
        return;
      }

      // Check for duplicates
      const existingFileNames = new Set(
        uploadedFiles.map((item) => item.file.name)
      );
      if (existingFileNames.has(file.name)) {
        alert(`The file "${file.name}" has already been uploaded.`);
        if (e.target) e.target.value = null;
        return;
      }

      const now = new Date();
      const formattedTime = now.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "numeric",
        hour12: true,
      });

      const newFileObject = {
        id: Date.now() + Math.random(),
        file: file,
        date: formattedTime,
        fileType: fileType,
        chapterId: selectedFile.id,
      };

      setUploadedFiles((prev) => [...prev, newFileObject]);

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
    [uploadedFiles, selectedFile, remainingFiles]
  );

  const handleFileDelete = useCallback((fileId, fileType, chapterId) => {
    setUploadedFiles((prev) => prev.filter((file) => file.id !== fileId));

    // If deleting similarity report, reset its status
    if (fileType === "Similarity Report") {
      setSimilarityReport({ file: null, percentage: null, status: null });
    }
  }, []);

  const handleDragEnter = useCallback((e) => {
    if (e && typeof e.preventDefault === "function") {
      e.preventDefault();
    }
    e.stopPropagation();
    dragCounter.current++;

    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  }, []);

  const handleDragLeave = useCallback((e) => {
    if (e && typeof e.preventDefault === "function") {
      e.preventDefault();
    }
    e.stopPropagation();
    dragCounter.current--;

    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e) => {
    if (e && typeof e.preventDefault === "function") {
      e.preventDefault();
    }
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e, fileType) => {
      if (e && typeof e.preventDefault === "function") {
        e.preventDefault();
      }
      e.stopPropagation();
      setIsDragging(false);
      dragCounter.current = 0;

      const files = Array.from(e.dataTransfer.files);

      const acceptedTypes = getAcceptedFileTypes(fileType);
      const validFiles = files.filter((file) => {
        const extension = "." + file.name.split(".").pop().toLowerCase();
        return acceptedTypes === "*/*" || acceptedTypes.includes(extension);
      });

      if (validFiles.length === 0) {
        alert(`Please drop ${acceptedTypes} files only`);
        return;
      }

      if (fileType === "Similarity Report") {
        const event = { target: { files: [validFiles[0]] } };
        handleReport(event);
      } else {
        const event = { target: { files: [validFiles[0]] } };
        handleFileUpload(event, fileType);
      }
    },
    [handleReport, handleFileUpload]
  );

  const getChapterStatusText = (chapterId) => {
    const status = chapterStatuses[chapterId];
    switch (status) {
      case "Approved":
        return "Approved";
      case "Pending":
        return "Under Review";
      case "Returned":
        return "Returned";
      case "Submitted":
        return "Submitted";
      case "active":
        return "Active";
      case "locked":
        return "Locked";
      default:
        return "";
    }
  };

  const getChapterStatusColor = (chapterId) => {
    const status = chapterStatuses[chapterId];
    switch (status) {
      case "Approved":
        return "#049300";
      case "Pending":
        return "#f59e0b";
      case "Returned":
        return "#dc2626";
      case "Submitted":
        return "#6366f1";
      case "active":
        return "#3b82f6";
      case "locked":
        return "#9ca3af";
      default:
        return "#6b7280";
    }
  };
  return (
    <SubmissionSection>
      {isLoading ? (
        <Loader />
      ) : ["Ongoing", "Returned", "Active", "Approved", "Submitted"].includes(
          assignmentDetails?.status
        ) ? (
        <div>
          <FileTypeSelection>
            <InnerFileTypeSelection>
              {fileArray?.map((item) => (
                <TabContainer
                  key={item.id}
                  selected={selectedFile.id === item.id}
                  locked={chapterStatuses[item.id] === "locked"}
                >
                  <FileType
                    style={{
                      backgroundColor:
                        selectedFile.id === item.id
                          ? "#B48CCC80"
                          : chapterStatuses[item.id] === "locked"
                          ? "#f3f4f6"
                          : "#ccbbd640",
                      minWidth: selectedFile.id === item.id ? "110px" : "80px",
                      maxWidth: selectedFile.id === item.id ? "250px" : "95px",
                      cursor:
                        chapterStatuses[item.id] === "locked"
                          ? "not-allowed"
                          : "pointer",
                    }}
                    onClick={() => handleFileSelection(item)}
                  >
                    {item.sectionName.slice(0, 1).toUpperCase() +
                      item.sectionName.slice(1)}
                  </FileType>
                </TabContainer>
              ))}
            </InnerFileTypeSelection>
          </FileTypeSelection>

          <AssignmentSubmissionContainer>
            <div
              style={{
                display: "flex",
                width: "100%",
                gap: "20px",
                flexWrap: "nowrap",
              }}
            >
              {selectedFile?.fileRequirement?.map((item, index) => {
                const remaining = remainingFiles[item.file_type] || 0;
                const latestStatus = getLatestSubmissionStatus(
                  selectedFile,
                  processedSubmissions,
                  globalStatus
                );
                const isChapterCompleted =
                  latestStatus === "Approved" || latestStatus === "Submitted";
                const isCompleted = remaining === 0 || isChapterCompleted;
                const canUpload =
                  !isChapterCompleted &&
                  (remaining > 0 ||
                    (item.file_type === "Similarity Report" &&
                      similarityReport.status !== "valid"));

                return (
                  <SubmittingContainer
                    key={index}
                    style={{
                      cursor: canUpload ? "pointer" : "default",
                      width:
                        selectedFile.sectionName === "Final Submission"
                          ? "48%"
                          : "100%",
                      backgroundColor:
                        isDragging && canUpload ? "#f0f8ff" : "transparent",
                      borderColor:
                        isDragging && canUpload ? "#4048AE" : "#9d9d9d",
                      minWidth: "300px",
                      opacity: isCompleted ? 0.8 : 1,
                    }}
                    onDragEnter={canUpload ? handleDragEnter : undefined}
                    onDragLeave={canUpload ? handleDragLeave : undefined}
                    onDragOver={canUpload ? handleDragOver : undefined}
                    onDrop={
                      canUpload
                        ? (e) => handleDrop(e, item.file_type)
                        : undefined
                    }
                  >
                    {isDragging && canUpload ? (
                      <DragActiveContent>
                        <FileIcon
                          style={{
                            width: "80px",
                            height: "80px",
                            opacity: 0.8,
                          }}
                          src={fileUploadIcon}
                          alt="file_upload_icon"
                        />
                        <DragText>
                          Drop your {item.file_type} file here
                        </DragText>
                      </DragActiveContent>
                    ) : (
                      <>
                        <FileIcon
                          style={{ width: "50px", height: "65px" }}
                          src={fileUploadIcon}
                          alt="file_upload_icon"
                        />
                        <UploadMainText>
                          <span style={{ color: "#4048AE" }}>
                            Drag and Drop
                          </span>{" "}
                          your {item.file_type} file here
                        </UploadMainText>

                        <FileCountDiv>
                          {isChapterCompleted ? (
                            <CompletedSection>
                              <CompletedText>completed</CompletedText>
                              <FileIcon
                                src={tickIconGreen}
                                alt="tick"
                                style={{
                                  height: "15px",
                                  width: "15px",
                                  marginLeft: "3px",
                                }}
                              />
                            </CompletedSection>
                          ) : item.file_type === "Similarity Report" &&
                            similarityReport.file === null ? (
                            <>
                              Drop your{" "}
                              <span style={{ color: "#DF0000" }}>PDF</span> file
                              here
                            </>
                          ) : item.file_type === "Similarity Report" &&
                            similarityReport.file !== null &&
                            similarityReport.status === "invalid" ? (
                            <InvalidReportContainer>
                              <ReuploadSection>
                                <div style={{ color: "#007BFF" }}>reupload</div>
                                <FileIcon
                                  src={reUploadIcon}
                                  alt="re-upload icon"
                                  style={{
                                    height: "15px",
                                    width: "15px",
                                    marginLeft: "3px",
                                  }}
                                />
                              </ReuploadSection>
                              <ErrorMessage>
                                The similarity index exceeds 10%. Please
                                re-upload the assignment files.
                              </ErrorMessage>
                            </InvalidReportContainer>
                          ) : item.file_type === "Similarity Report" &&
                            similarityReport.file !== null &&
                            similarityReport.status === "valid" ? (
                            <CompletedSection>
                              <CompletedText>completed</CompletedText>
                              <FileIcon
                                src={tickIconGreen}
                                alt="tick"
                                style={{
                                  height: "15px",
                                  width: "15px",
                                  marginLeft: "3px",
                                }}
                              />
                            </CompletedSection>
                          ) : isCompleted ? (
                            <CompletedSection>
                              <CompletedText>completed</CompletedText>
                              <FileIcon
                                src={tickIconGreen}
                                alt="tick"
                                style={{
                                  height: "15px",
                                  width: "15px",
                                  marginLeft: "3px",
                                }}
                              />
                            </CompletedSection>
                          ) : (
                            <>
                              Drop {item.file_type.charAt(0).toUpperCase()}
                              {item.file_type.slice(1)} File
                            </>
                          )}
                        </FileCountDiv>

                        {canUpload && !isChapterCompleted && (
                          <UploadButton>
                            <UploadButtonText>Choose file</UploadButtonText>
                            <input
                              ref={fileInputRef}
                              type="file"
                              accept={getAcceptedFileTypes(item.file_type, selectedFile.fileRequirement)}
                              style={{ display: "none" }}
                              onChange={(e) =>
                                item.file_type === "Similarity Report"
                                  ? handleReport(e)
                                  : handleFileUpload(e, item.file_type)
                              }
                            />
                          </UploadButton>
                        )}
                      </>
                    )}
                  </SubmittingContainer>
                );
              })}
            </div>

            <LoadedFilesDiv>
              <FilesGrid>
                {uploadedFiles.map((item) => (
                  <LoadedFileDiv
                    key={item.id}
                    file={item}
                    onDelete={handleFileDelete}
                    selectedFileId={selectedFile.id}
                    fileTypeToDelete={item.fileType}
                  />
                ))}
              </FilesGrid>
              <SubmitButtonContainer>
                <SubmitButton
                  style={{
                    backgroundColor: submitButton ? "#917AFD" : "#828282ff",
                    opacity: submitButton ? 1 : 0.6,
                  }}
                  disabled={!submitButton}
                  onClick={() => submitAssignmentFile()}
                >
                  Submit
                </SubmitButton>
              </SubmitButtonContainer>
            </LoadedFilesDiv>
          </AssignmentSubmissionContainer>
          <SubmissionsSection>
            <SubmissionsViewContainer>
              {getSelectedChapterSubmissions()
                .sort(
                  (a, b) =>
                    new Date(b.submittedDate) - new Date(a.submittedDate)
                )
                .map((submission, index) => (
                  <VersionAccordion key={submission.version}>
                    <VersionHeader
                      onClick={() =>
                        handleVersionToggle(
                          selectedFile.sectionName === "Draft Submission"
                            ? "draft"
                            : selectedFile.sectionName === "Final Submission"
                            ? "final"
                            : selectedFile.id,
                          submission.version
                        )
                      }
                    >
                      <VersionInfo>
                        <VersionTitle>
                          {submission.chapter === "draft" ? (
                            <>Draft Submission</>
                          ) : submission.chapter === "final" ? (
                            <>Final Submission</>
                          ) : (
                            <>Chapter {submission.chapter}</>
                          )}
                        </VersionTitle>

                        <VersionMeta>
                          <span>
                            Submitted: {timeFormatter(submission.submittedDate)}
                          </span>
                          <StatusIndicator status={submission.status}>
                            {submission.status === "Returned"
                              ? "Modify"
                              : submission.status}
                          </StatusIndicator>
                        </VersionMeta>
                      </VersionInfo>
                      <ArrowIcon
                        expanded={
                          expandedVersions[
                            `${
                              selectedFile.sectionName === "Draft Submission"
                                ? "draft"
                                : selectedFile.sectionName ===
                                  "Final Submission"
                                ? "final"
                                : selectedFile.id
                            }-${submission.version}`
                          ]
                        }
                      >
                        <img src={downArrow} alt="arrow" />
                      </ArrowIcon>
                    </VersionHeader>

                    {expandedVersions[
                      `${
                        selectedFile.sectionName === "Draft Submission"
                          ? "draft"
                          : selectedFile.sectionName === "Final Submission"
                          ? "final"
                          : selectedFile.id
                      }-${submission.version}`
                    ] && (
                      <VersionContent>
                        <SubmissionDetails>
                          {submission.feedback && (
                            <FeedbackSection>
                              <FeedbackLabel>
                                Feedback
                                {submission.feedbackDate && (
                                  <span
                                    style={{
                                      fontWeight: "400",
                                      fontSize: "12px",
                                      color: "#656565",
                                      marginLeft: "10px",
                                      marginBottom: "5px",
                                    }}
                                  >
                                    {timeFormatter(submission.feedbackDate)}
                                  </span>
                                )}
                              </FeedbackLabel>
                              <FeedbackDisplay>
                                {submission.feedback}
                              </FeedbackDisplay>
                            </FeedbackSection>
                          )}

                          {submission.upload && (
                            <FacultyFeedbackSection>
                              <FileSectionTitle>
                                Faculty Feedback File
                                <FileSectionSubTitle>
                                  {" "}
                                  (Download the file to view the detailed
                                  faculty feedback.){" "}
                                </FileSectionSubTitle>
                              </FileSectionTitle>

                              <SubmittedFilesGrid>
                                <SubmittedFileCard
                                  href={submission.upload}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <FileIconWrapper>
                                    <img
                                      src={getFileIcon(
                                        submission.feedback_file_type
                                      )}
                                      alt={`${submission.feedback_file_type} file`}
                                    />
                                  </FileIconWrapper>
                                  <SubmittedFileDetails>
                                    <SubmittedFileName>
                                      {submission.feedback_file_name}
                                    </SubmittedFileName>
                                    <SubmittedFileType>
                                      {submission.feedback_file_type}
                                    </SubmittedFileType>
                                  </SubmittedFileDetails>
                                </SubmittedFileCard>
                              </SubmittedFilesGrid>
                            </FacultyFeedbackSection>
                          )}
                          <FilesSection>
                            <div>
                              <FileSectionTitle>Student File</FileSectionTitle>
                              <SubmittedFilesGrid>
                                {submission.files.map((file, fileIndex) => (
                                  <SubmittedFileCard
                                    key={fileIndex}
                                    href={file.fileUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    <FileIconWrapper>
                                      <img
                                        src={getFileIcon(file.fileType)}
                                        alt={`${file.fileType} file`}
                                      />
                                    </FileIconWrapper>
                                    <SubmittedFileDetails>
                                      <SubmittedFileName>
                                        {file.fileName}
                                      </SubmittedFileName>
                                      <SubmittedFileType>
                                        {file.fileType}
                                      </SubmittedFileType>
                                    </SubmittedFileDetails>
                                  </SubmittedFileCard>
                                ))}
                              </SubmittedFilesGrid>
                            </div>
                          </FilesSection>
                        </SubmissionDetails>
                      </VersionContent>
                    )}
                  </VersionAccordion>
                ))}
            </SubmissionsViewContainer>
          </SubmissionsSection>
        </div>
      ) : (
        <EmptyStateContainer>
          <EmptyStateText>
            Assignment is not available for submission
          </EmptyStateText>
        </EmptyStateContainer>
      )}
    </SubmissionSection>
  );
};

// ============= STYLED COMPONENTS =============

const SubmissionSection = styled.div`
  margin-top: 30px;
`;

const AssignmentSubmissionContainer = styled.div`
  border: 1px solid #9d9d9d;
  margin-top: -1px;
  border-radius: 10px;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: start;
  align-items: center;
  padding: 20px 30px;
  transition: all 0.3s ease;
`;

const SubmissionsSection = styled.div`
  margin-top: 30px;
  border-radius: 12px;
  overflow: hidden;
`;

const SubmissionsViewContainer = styled.div`
  padding: 20px 30px;
  max-height: 600px;
  overflow-y: auto;
  background: white;
`;

const VersionAccordion = styled.div`
  background: #f9fafb;
  border-radius: 12px;
  margin-bottom: 16px;
  overflow: hidden;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);

  &:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const VersionHeader = styled.div`
  display: flex;
  background: #f9fafb;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  cursor: pointer;
  transition: all 0.3s ease;
`;

const VersionInfo = styled.div`
  flex: 1;
`;

const VersionTitle = styled.div`
  font-weight: 600;
  font-size: 16px;
  line-height: 160%;
  color: #1f2937;
  margin-bottom: 4px;
`;

const VersionMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-weight: 400;
  font-size: 12px;
  line-height: 160%;
  color: #656565;
`;

const StatusIndicator = styled.div`
  font-weight: 500;
  font-size: 12px;
  padding: 4px 8px;
  border-radius: 6px;
  background: ${(props) => {
    switch (props.status?.toLowerCase()) {
      case "approved":
        return "#dcfce7";
      case "returned":
        return "#fee2e2";
      case "pending":
        return "#fef3c7";
      case "submitted":
        return "#dbeafe";
      default:
        return "#f3f4f6";
    }
  }};
  color: ${(props) => {
    switch (props.status?.toLowerCase()) {
      case "approved":
        return "#16a34a";
      case "returned":
        return "#dc2626";
      case "pending":
        return "#d97706";
      case "submitted":
        return "#2563eb";
      default:
        return "#6b7280";
    }
  }};
`;

const ArrowIcon = styled.div`
  width: 22px;
  height: 14px;
  transition: transform 0.3s ease;
  transform: ${(props) => (props.expanded ? "rotate(180deg)" : "rotate(0deg)")};

  img {
    width: 100%;
    height: 100%;
  }
`;

const VersionContent = styled.div`
  padding: 0 24px;
`;

const SubmissionDetails = styled.div`
  background: #f9fafb;
  border-radius: 8px;
  padding: 0 16px 16px 0;
`;

const FilesSection = styled.div`
  margin-bottom: 20px;
  display: flex;
  gap: 40px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 20px;
  }
`;

const FileSectionTitle = styled.div`
  font-weight: 600;
  font-size: 16px;
  color: #1e293b;
  margin-bottom: 12px;
  line-height: 1.4;
`;

const FileSectionSubTitle = styled.span`
  font-weight: 400;
  font-size: 14px;
  color: #64748b;
  margin-left: 2px;
`;

const SubmittedFilesGrid = styled.div`
  display: flex;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
`;

const SubmittedFileCard = styled.a`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  text-decoration: none;
  color: inherit;
  transition: all 0.3s ease;

  &:hover {
    background: linear-gradient(to right, #f0f4ff, #e8f0ff);
    border-color: #c7d2fe;
    box-shadow: 0 4px 12px rgba(99, 102, 241, 0.15);
  }
`;

const FileIconWrapper = styled.div`
  flex-shrink: 0;

  img {
    width: 24px;
    height: 24px;
    object-fit: contain;
  }
`;

const SubmittedFileDetails = styled.div`
  flex: 1;
  min-width: 0;
`;

const SubmittedFileName = styled.div`
  font-weight: 500;
  font-size: 13px;
  color: #1f2937;
  margin-bottom: 2px;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SubmittedFileType = styled.div`
  font-size: 11px;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const FeedbackSection = styled.div`
  margin-top: 16px;
  margin-bottom: 1rem;
`;

const FacultyFeedbackSection = styled.div`
  margin-top: 16px;
  margin-bottom: 1rem;
`;

const FeedbackLabel = styled.div`
  font-weight: 600;
  font-size: 16px;
  color: #1e293b;
  margin-bottom: 12px;
  line-height: 1.4;
`;

const FeedbackDisplay = styled.div`
  padding: 12px 16px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  color: #374151;
  line-height: 1.5;
`;

const FileTypeSelection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
`;

const InnerFileTypeSelection = styled.div`
  min-width: 70%;
  display: flex;
  justify-content: space-evenly;
  align-items: center;
  gap: 4px;
  flex-wrap: nowrap;
`;

const TabContainer = styled.div`
  border: ${(props) => (props.selected ? "1px solid #9d9d9d" : "none")};
  border-bottom: ${(props) => (props.selected ? "2px solid white" : "none")};
  border-radius: ${(props) => (props.selected ? "14px 14px 0 0" : "0")};
  padding: ${(props) => (props.selected ? "5px 5px 0 5px" : "0")};
  opacity: ${(props) => (props.locked ? "0.5" : "1")};
`;

const FileType = styled.div`
  height: 45px;
  padding: 10px;
  text-align: center;
  color: #313131;
  border-radius: 9px;
  cursor: pointer;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    // transform: translate(-1px,-1px);
  }
`;

const SubmittingContainer = styled.label`
  border: 2px dashed #9d9d9d;
  box-sizing: border-box;
  border-radius: 10px;
  height: 350px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 30px 0;
  margin-bottom: 20px;
  transition: all 0.3s ease;

  &:hover {
    border-color: #4048ae;
    background: #f8faff;
  }
`;

const DragActiveContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  transition: all 0.3s ease;
`;

const DragText = styled.div`
  font-size: 24px;
  color: #4048ae;
  font-weight: bold;
  margin-top: 20px;
  transition: all 0.3s ease;
`;

const UploadMainText = styled.div`
  font-size: 22.25px;
  line-height: 160%;
  transition: all 0.3s ease;
`;

const FileCountDiv = styled.div`
  color: #797979;
  text-align: center;
  font-size: 18px;
  line-height: 160%;
  transition: all 0.3s ease;
`;

const UploadButton = styled.label`
  padding: 12px 10px;
  border-radius: 9px;
  width: 120px;
  background: #e9e9e9;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: #d4d4d4;
  }
`;

const UploadButtonText = styled.p`
  font-size: 14px;
  font-weight: 500;
  margin-bottom: -2px;
  color: #000000;
`;

const LoadedFilesDiv = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
  margin-top: 20px;
`;

const FilesGrid = styled.div`
  width: 100%;
  gap: 10px;
  display: flex;
  flex-wrap: wrap;
`;

const SubmitButtonContainer = styled.div`
  margin-right: 10px;
  display: flex;
  justify-content: center;
  margin-top: 20px;
  align-items: start;
`;

const SubmitButton = styled.button`
  display: inline-block;
  height: 40px;
  padding: 12px 20px;
  background: #917afd;
  border-radius: 6px;
  font-size: 16px;
  font-weight: 500;
  letter-spacing: 0.05rem;
  color: #fff;
  cursor: pointer;
  border: none;
  transition: all 0.3s ease;

  &:hover:not(:disabled) {
    background: #8069e8;
    box-shadow: 0 4px 8px rgba(145, 122, 253, 0.3);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
    background: #828282ff;
  }
`;

const FileCard = styled.div`
  box-shadow: 2px 2px 5px rgba(0, 0, 0, 0.25);
  min-width: 220px;
  height: 70px;
  border-radius: 8px;
  display: flex;
  padding: 10px;
  justify-content: space-between;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 3px 3px 8px rgba(0, 0, 0, 0.3);
  }
`;

const FileCardLink = styled.a`
  text-decoration: none;
  color: inherit;
  display: flex;
  align-items: center;
  flex-direction: column;
  flex: 1;
`;

const FileCardContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-evenly;
  flex: 1;
`;

const FileInfo = styled.div``;

const FileName = styled.div`
  font-size: 15px;
  font-weight: bold;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const FileMeta = styled.div`
  display: flex;
`;

const FileSize = styled.div`
  font-size: 12px;
  margin-right: 10px;
`;

const FileDate = styled.div`
  font-size: 12px;
`;

const DeleteButton = styled.div`
  width: 20%;
  display: flex;
  justify-content: end;
  align-items: start;
`;

const FileIcon = styled.img`
  width: 30px;
  height: 30px;
  object-fit: contain;
  margin-right: 8px;
`;

const CompletedSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: #09c425;
  transition: all 0.3s ease;
`;

const CompletedText = styled.div`
  color: #09c425;
  font-size: 18px;
  font-style: normal;
  font-weight: 500;
  line-height: 160%;
  letter-spacing: 0.72px;
`;

const InvalidReportContainer = styled.div`
  text-align: center;
`;

const ReuploadSection = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: center;
`;

const ErrorMessage = styled.div`
  color: red;
  margin-bottom: 2px;
`;

const EmptyStateContainer = styled.div`
  width: 100%;
  text-align: center;
  padding: 40px;
`;

const EmptyStateText = styled.div`
  color: #666;
  font-size: 16px;
`;

export default SubmitChapterWise;
